import {
  MdOutlineHome,
  MdOutlineCalendarMonth,
  MdOutlineContacts,
  MdOutlinePeopleAlt,
  MdOutlineInventory2,
  MdOutlineDirectionsCar,
  MdOutlineCarRepair,
  MdOutlineShoppingBag,
  MdOutlineAccountBalance,
  MdOutlineReceiptLong,
  MdOutlineAnalytics,
} from "react-icons/md";
import { flattenNav } from "../Sidebar/sidebarNavConfig";

/**
 * New menu (module rail + page panel): regroups what getNavGroups() returns.
 *
 * Access rules are NOT repeated here. getNavGroups() has already dropped every
 * page this person's role, plan, industry and module permissions don't allow,
 * so this file only decides where each remaining page sits. A section with no
 * pages left disappears, and so does a module.
 *
 * Every page lands somewhere: a page not listed below falls back to the module
 * its old group maps to (GROUP_FALLBACK), so a new page added to
 * sidebarNavConfig.js still shows up before anyone updates this layout.
 */

const SETTINGS_KEYS = [
  "admin-setup",
  "branding",
  "hr-settings",
  "inventory-settings",
  "dms-setup",
  "proc-setup",
  "fin-setup",
  "fin-erp",
  "expense-management-settings",
];

// Clearer names in the new menu only. The old menu keeps its labels.
const RENAMES = {
  "dms-purchasing": "Vehicle & Parts Purchasing",
  "proc-orders": "Awarded Orders",
  // Portal invites, portal users and documents. The supplier master stays at Inventory > Suppliers.
  "proc-suppliers": "Supplier Portal",
  "inventory-settings": "Inventory Settings",
  "expense-management-settings": "Branch Jobs Setup",
};

const DMS_SALES_SECTIONS = [
  { title: "Vehicle sales", keys: ["dms-dashboard", "dms-availability", "dms-enquiries", "dms-quotes", "dms-orders", "dms-test-drives", "dms-trade-ins", "dms-fi", "dms-fleet"] },
  { title: "Vehicles", keys: ["dms-stock", "dms-pdi", "dms-purchasing", "dms-transfers", "dms-returns"] },
];
const DMS_AFTER_SECTIONS = [
  { title: "Workshop", keys: ["dms-as-dash", "dms-ro", "dms-board", "dms-bodyshop", "dms-warranty"] },
  { title: "Parts & counter", keys: ["dms-parts", "dms-counter", "dms-pdc"] },
  { title: "Customer care", keys: ["dms-recalls", "dms-reminders", "dms-csi"] },
];

function layout(splitDealership) {
  const dealership = splitDealership
    ? [
        { id: "vehicle-sales", label: "Vehicle Sales", icon: MdOutlineDirectionsCar, sections: DMS_SALES_SECTIONS },
        { id: "after-sales", label: "After-sales", icon: MdOutlineCarRepair, sections: DMS_AFTER_SECTIONS },
      ]
    : [{ id: "dealership", label: "Dealership", icon: MdOutlineDirectionsCar, sections: [...DMS_SALES_SECTIONS, ...DMS_AFTER_SECTIONS] }];

  return [
    { id: "home", label: "Home", icon: MdOutlineHome, sections: [{ title: null, keys: ["dashboard", "approvals"] }] },
    {
      id: "scheduling",
      label: "Scheduling",
      icon: MdOutlineCalendarMonth,
      sections: [
        { title: "Daily", keys: ["new-booking", "calendar", "tasks"] },
        { title: "Sell", keys: ["estimations", "bid-requests", "subscription"] },
      ],
    },
    {
      id: "customers",
      label: "Customers",
      icon: MdOutlineContacts,
      sections: [
        { title: null, keys: ["customers", "leads"] },
        { title: "Engage", keys: ["channels", "campaigns", "ai-assistant"] },
      ],
    },
    {
      id: "people",
      label: "People",
      icon: MdOutlinePeopleAlt,
      sections: [
        { title: null, keys: ["hr-overview"] },
        { title: "Team", keys: ["providers", "employees", "hr-employees", "hr-org-chart", "hr-documents"] },
        { title: "Time & leave", keys: ["hr-attendance", "hr-roster", "hr-leave", "hr-approvals"] },
        { title: "Pay", keys: ["hr-payroll"] },
        { title: "Me", keys: ["hr-my-space", "hr-payslips"] },
      ],
    },
    {
      id: "inventory",
      label: "Inventory",
      icon: MdOutlineInventory2,
      sections: [
        { title: null, keys: ["inventory-items", "inventory-stock", "inventory-low-stock"] },
        { title: "Movements", keys: ["inventory-transfers", "inventory-counts"] },
        { title: "Purchasing", keys: ["inventory-suppliers", "inventory-po", "inventory-grn", "inventory-reorder"] },
        { title: "Insights", keys: ["inventory-reports", "inventory-analytics"] },
      ],
    },
    ...dealership,
    {
      id: "procurement",
      label: "Procurement",
      icon: MdOutlineShoppingBag,
      sections: [{ title: null, keys: ["proc-dashboard", "proc-rfqs", "proc-awards", "proc-orders", "proc-messages", "proc-suppliers"] }],
    },
    {
      id: "finance",
      label: "Finance",
      icon: MdOutlineAccountBalance,
      sections: [
        { title: null, keys: ["fin-dashboard"] },
        { title: "Money in", keys: ["payments", "fin-receivables"] },
        { title: "Money out", keys: ["fin-payables", "opex"] },
        { title: "Bank", keys: ["fin-cash", "fin-bankrec"] },
        { title: "Accounting", keys: ["fin-journals", "fin-coa", "fin-assets", "fin-projects", "fin-periods"] },
        { title: "Tax & reports", keys: ["fin-tax", "fin-reports"] },
      ],
    },
    // Branch keys carry the branch id (expense-management-branch-<id>), so they're matched by prefix below.
    { id: "branch-jobs", label: "Branch Jobs", icon: MdOutlineReceiptLong, sections: [{ title: "Branches", keys: [] }] },
    { id: "reports", label: "Reports", icon: MdOutlineAnalytics, sections: [{ title: null, keys: ["analytics", "reports"] }] },
  ];
}

// Where a page goes when the layout above doesn't list it, by its old group.
const GROUP_FALLBACK = {
  null: "home",
  scheduling: "scheduling",
  customers: "customers",
  growth: "customers",
  workforce: "people",
  hr: "people",
  inventory: "inventory",
  procurement: "procurement",
  finance: "finance",
  "expense-management": "branch-jobs",
  insights: "reports",
};

export const SETTINGS_MODULE_ID = "settings";

/**
 * @param navGroups output of getNavGroups() (already filtered for this person)
 * @param opts.splitDealership  true: "Vehicle Sales" + "After-sales"; false: one "Dealership"
 * @returns {{ modules: Module[], settings: Item[], items: Item[] }}
 *   Module = { id, label, icon, sections: [{ title, items }], items, single }
 *   Item   = the nav item, plus moduleId / moduleLabel / section
 *   `single` is set when a module holds one page: the rail then opens it directly.
 */
export function getNavModules(navGroups, { splitDealership = true } = {}) {
  const flat = flattenNav(navGroups);
  const present = new Set(flat.map((i) => i.key));

  // One inbox: the shared Approvals page already lists HR requests, so HR > Approvals
  // is dropped whenever the shared one is on offer.
  const hidden = new Set();
  if (present.has("approvals")) hidden.add("hr-approvals");

  const byKey = new Map();
  flat.forEach((item) => {
    if (hidden.has(item.key) || byKey.has(item.key)) return;
    byKey.set(item.key, { ...item, label: RENAMES[item.key] || item.label });
  });

  const placed = new Set();
  const take = (key) => {
    const item = byKey.get(key);
    if (!item || placed.has(key)) return null;
    placed.add(key);
    return item;
  };

  const settings = SETTINGS_KEYS.map(take).filter(Boolean);

  const modules = layout(splitDealership).map((mod) => ({
    ...mod,
    sections: mod.sections.map((sec) => ({ title: sec.title, items: sec.keys.map(take).filter(Boolean) })),
  }));

  const findModule = (id) => modules.find((m) => m.id === id);

  // Branch Jobs: one page per branch.
  const branchJobs = findModule("branch-jobs");
  flat.forEach((item) => {
    if (item.key.startsWith("expense-management-branch-")) {
      const it = take(item.key);
      if (it) branchJobs.sections[0].items.push(it);
    }
  });

  // Anything still unplaced goes to its old group's module, in a trailing section.
  byKey.forEach((item, key) => {
    if (placed.has(key)) return;
    placed.add(key);
    const dealershipTarget = splitDealership ? "vehicle-sales" : "dealership";
    const targetId = item.groupKey === "dealership" ? dealershipTarget : item.groupKey === "settings" ? null : GROUP_FALLBACK[item.groupKey] || "home";
    if (!targetId) {
      settings.push(item);
      return;
    }
    const target = findModule(targetId);
    let more = target.sections.find((s) => s.title === "More");
    if (!more) {
      more = { title: "More", items: [] };
      target.sections.push(more);
    }
    more.items.push(item);
  });

  const result = modules
    .map((mod) => {
      const sections = mod.sections.filter((s) => s.items.length > 0);
      const items = sections.flatMap((s) => s.items);
      // Name a one-page module after its page ("Patients", "Doctor"), except Home.
      const single = items.length === 1 && mod.id !== "home";
      const label = single ? items[0].label : mod.id === "customers" && byKey.get("customers") ? byKey.get("customers").label : mod.label;
      return { ...mod, label, sections, items, single };
    })
    .filter((mod) => mod.items.length > 0);

  const tag = (mod) => (sec) => sec.items.forEach((it) => Object.assign(it, { moduleId: mod.id, moduleLabel: mod.label, section: sec.title }));
  result.forEach((mod) => mod.sections.forEach(tag(mod)));
  settings.forEach((it) => Object.assign(it, { moduleId: SETTINGS_MODULE_ID, moduleLabel: "Settings", section: null }));

  return { modules: result, settings, items: [...result.flatMap((m) => m.items), ...settings] };
}

// Path part only (a few links carry a query, e.g. /fin/cash?tab=cheques).
const pathOf = (to) => String(to).split("?")[0];

/** Exact match for an item whose path is a prefix of another item's (Inventory "Items" = /inventory). */
export function needsExactMatch(item, all) {
  const p = pathOf(item.to);
  return all.some((other) => other !== item && pathOf(other.to).startsWith(`${p}/`));
}

/** The nav item for the current path: exact match first, else the longest prefix. */
export function itemForPath(pathname, items) {
  let best = null;
  items.forEach((item) => {
    const p = pathOf(item.to);
    const hit = pathname === p || (pathname.startsWith(`${p}/`) && !needsExactMatch(item, items));
    if (hit && (!best || p.length > pathOf(best.to).length)) best = item;
  });
  return best;
}

const MAX_RESULTS = 40;

/** "Jump to" search: every query word must appear in the page name, module or section. */
export function searchItems(items, query) {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const scored = [];
  items.forEach((item) => {
    const label = item.label.toLowerCase();
    const hay = `${label} ${String(item.moduleLabel || "").toLowerCase()} ${String(item.section || "").toLowerCase()}`;
    if (!words.every((w) => hay.includes(w))) return;
    // Label starts with the query > label contains it > only module/section match.
    const q = words.join(" ");
    const score = label.startsWith(q) ? 0 : label.includes(q) ? 1 : 2;
    scored.push({ item, score });
  });
  scored.sort((a, b) => a.score - b.score);
  return scored.slice(0, MAX_RESULTS).map((s) => s.item);
}

